import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import Seo from "../Components/Seo.jsx";
import PageHeader from "../Components/PageHeader.jsx";
import { ContactLines } from "../Components/SiteInfo.jsx";
import { trackEvent } from "../utils/analytics.js";
import { ORG } from "../seo/site.js";

// Donation form. The donor's details are sent to public/donate-checkout.php,
// which validates and saves them, then creates a Stripe Checkout session for
// the entered amount (one-off payment or monthly subscription). The donor pays
// on Stripe's hosted page. Stripe keys and the database live on the server
// only. Nothing about tax deductibility or bank transfers is stated: those
// are awaiting WSTSC confirmation (OWNER_INPUT_NEEDED.md).

const PATH = "/donate/";
const ENDPOINT = "/donate-checkout.php";
const PRESETS = [25, 50, 100, 250];

// Same rules as validate_donation() in donate-checkout.php (the server is the
// authority; these only give instant feedback).
const NAME = /^[\p{L}\p{M}][\p{L}\p{M}' .-]{1,99}$/u;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const emptyForm = { firstName: "", lastName: "", email: "", phone: "", frequency: "once" };

const Donate = () => {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled") === "1";

  // null = checking, then { configured, livemode, min, max }
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [preset, setPreset] = useState(50);
  const [custom, setCustom] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelledFetch = false;
    fetch(ENDPOINT, { headers: { Accept: "application/json" }, signal: AbortSignal.timeout(10000) })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error("unavailable"))))
      .then((data) => !cancelledFetch && setConfig(data))
      .catch(() => !cancelledFetch && setConfig({ configured: false }));
    return () => {
      cancelledFetch = true;
    };
  }, []);

  const min = config?.min ?? 5;
  const max = config?.max ?? 10000;
  const amount = custom !== "" ? Number(custom) : preset;
  const amountValid = Number.isInteger(amount) && amount >= min && amount <= max;
  const monthly = form.frequency === "monthly";
  // On the live site the form appears only once Stripe is configured. In
  // `npm run dev` it is always shown, so it can be designed and reviewed
  // before a test key is added (submitting then reports it isn't connected).
  const previewOnly = import.meta.env.DEV && config !== null && !config.configured;
  const showForm = Boolean(config?.configured) || previewOnly;

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "" }));
    setFormError("");
  };

  const validate = () => {
    const found = {};
    if (!NAME.test(form.firstName.trim())) found.firstName = "Please enter your first name.";
    if (!NAME.test(form.lastName.trim())) found.lastName = "Please enter your last name.";
    if (!EMAIL.test(form.email.trim())) found.email = "Please enter a valid email address.";
    if (!/^\d{8,15}$/.test(form.phone)) found.phone = "Please enter a valid phone number.";
    if (!amountValid) found.amount = `Please enter a whole dollar amount between $${min} and $${max.toLocaleString("en-AU")}.`;
    return found;
  };

  const donate = async (event) => {
    event.preventDefault();
    setFormError("");
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) {
      document.getElementById(`donate-${Object.keys(found)[0]}`)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          phone: `+${form.phone}`,
          frequency: form.frequency,
          amount,
        }),
        signal: AbortSignal.timeout(25000),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 422 && data.fields) {
        setErrors(data.fields);
        setSubmitting(false);
        return;
      }
      if (!response.ok || !data.url) throw new Error(data.error || "unavailable");
      // Analytics: amount and type only - never the donor's details.
      trackEvent("begin_checkout", { currency: "AUD", value: amount, item_name: monthly ? "monthly donation" : "donation" });
      window.location.assign(data.url);
    } catch {
      setSubmitting(false);
      setFormError(
        `Sorry, we couldn't start the payment and you have not been charged. Please try again, or contact us on ${ORG.phones.committee.display} or ${ORG.email}.`,
      );
    }
  };

  const textField = (name, label, props = {}) => (
    <div className="col-md-6">
      <label htmlFor={`donate-${name}`} className="form-label fw-bold">
        {label} <span className="text-danger" aria-hidden="true">*</span>
      </label>
      <input
        id={`donate-${name}`}
        className={`form-control ${errors[name] ? "is-invalid" : ""}`}
        value={form[name]}
        onChange={(event) => setField(name, event.target.value)}
        aria-invalid={errors[name] ? "true" : undefined}
        aria-describedby={errors[name] ? `donate-${name}-error` : undefined}
        required
        {...props}
      />
      {errors[name] && (
        <div id={`donate-${name}-error`} className="invalid-feedback d-block">
          {errors[name]}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Seo path={PATH} />
      <PageHeader
        title="Support Our Tamil School"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house" },
          { label: "Donate", active: true },
        ]}
      />

      <section className="py-5">
        <div className="container py-lg-4">
          <div className="row g-4 g-lg-5">
            <div className="col-lg-5">
              <p className="text-uppercase fw-bold red-text mb-2">Donate to WSTSC</p>
              <p className="fs-5 mb-3">
                {ORG.name} is a not-for-profit community language school, run
                with the help of parents, volunteers and community leaders.
              </p>
              <p className="text-muted mb-4">
                Your donation supports our Tamil language and cultural programs
                for children in Western Sydney. Every contribution, large or
                small, helps us keep Tamil language and heritage alive for the
                next generation.
              </p>

              <h2 className="fs-4 fw-bold mb-3">How your donation works</h2>
              <ul className="list-unstyled mb-4">
                <li className="d-flex gap-3 mb-3">
                  <i className="bi bi-shield-lock fs-4 blue-text flex-shrink-0" aria-hidden="true"></i>
                  <span className="text-muted">
                    Payment is made on Stripe's secure page. WSTSC never sees or
                    stores your card details.
                  </span>
                </li>
                <li className="d-flex gap-3 mb-3">
                  <i className="bi bi-arrow-repeat fs-4 blue-text flex-shrink-0" aria-hidden="true"></i>
                  <span className="text-muted">
                    Give once, or choose a monthly donation. You can cancel a
                    monthly donation at any time by contacting us.
                  </span>
                </li>
                <li className="d-flex gap-3 mb-3">
                  <i className="bi bi-envelope-check fs-4 blue-text flex-shrink-0" aria-hidden="true"></i>
                  <span className="text-muted">Stripe emails you a payment receipt.</span>
                </li>
                <li className="d-flex gap-3">
                  <i className="bi bi-info-circle fs-4 blue-text flex-shrink-0" aria-hidden="true"></i>
                  <span className="text-muted">
                    Need a formal receipt, or have a question about tax
                    deductibility? Please{" "}
                    <Link to="/contact/" className="red-text fw-semibold">contact us</Link>{" "}
                    before you donate.
                  </span>
                </li>
              </ul>

              <h2 className="fs-4 fw-bold mb-3">Other ways to give</h2>
              <p className="text-muted">
                To donate another way, or to talk about sponsoring an event,
                please get in touch.
              </p>
              <ContactLines />
            </div>

            <div className="col-lg-7">
              <div className="donate-card bg-white border rounded-4 border-bottom-left shadow-sm p-4 p-md-5">
                <h2 className="fs-3 fw-bold mb-1">Make a donation</h2>
                <p className="text-muted mb-4">
                  Fields marked <span className="text-danger">*</span> are required. Amounts are in Australian dollars (AUD).
                </p>

                {cancelled && (
                  <div className="alert alert-info small" role="status">
                    Your donation was not completed and you have not been
                    charged. You can try again below.
                  </div>
                )}

                {config?.configured && config.livemode === false && (
                  <div className="alert alert-warning small" role="status">
                    Test mode: no real money is taken. Use Stripe's test card
                    4242 4242 4242 4242.
                  </div>
                )}

                {config === null && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-secondary" role="status">
                      <span className="visually-hidden">Loading…</span>
                    </div>
                  </div>
                )}

                {previewOnly && (
                  <div className="alert alert-warning small" role="status">
                    Developer preview: Stripe isn't connected. Add a test key to{" "}
                    <code>wstsc-private/stripe.php</code> (see docs/donations.md) to
                    take test payments. Visitors to the live site see "Online
                    donations are being set up" instead of this form.
                  </div>
                )}

                {config && !config.configured && !previewOnly && (
                  <div className="alert alert-secondary mb-0" role="status">
                    <p className="fw-semibold mb-1">Online donations are being set up.</p>
                    <p className="mb-0 small">
                      In the meantime, please contact us on{" "}
                      <a href={`tel:${ORG.phones.committee.tel}`} className="red-text fw-semibold">
                        {ORG.phones.committee.display}
                      </a>{" "}
                      or{" "}
                      <a href={`mailto:${ORG.email}`} className="red-text fw-semibold">{ORG.email}</a>{" "}
                      to make a donation.
                    </p>
                  </div>
                )}

                {showForm && (
                  <form onSubmit={donate} noValidate>
                    <div className="row g-3 mb-4">
                      {textField("firstName", "First name", { autoComplete: "given-name", maxLength: 100 })}
                      {textField("lastName", "Last name", { autoComplete: "family-name", maxLength: 100 })}
                      {textField("email", "Email address", { type: "email", autoComplete: "email", maxLength: 190 })}
                      <div className="col-md-6">
                        <label htmlFor="donate-phone" className="form-label fw-bold">
                          Phone number <span className="text-danger" aria-hidden="true">*</span>
                        </label>
                        <PhoneInput
                          country="au"
                          value={form.phone}
                          onChange={(phone) => setField("phone", phone)}
                          placeholder="+61 4xx xxx xxx"
                          inputProps={{
                            id: "donate-phone",
                            name: "phone",
                            autoComplete: "tel",
                            required: true,
                            "aria-invalid": errors.phone ? "true" : undefined,
                            "aria-describedby": errors.phone ? "donate-phone-error" : undefined,
                          }}
                          inputClass={`w-100 ${errors.phone ? "is-invalid" : ""}`}
                          containerClass="w-100"
                          specialLabel=""
                        />
                        {errors.phone && (
                          <div id="donate-phone-error" className="invalid-feedback d-block">{errors.phone}</div>
                        )}
                      </div>
                    </div>

                    <fieldset className="mb-4">
                      <legend className="form-label fw-bold fs-6">
                        Donation type <span className="text-danger" aria-hidden="true">*</span>
                      </legend>
                      <div className="row g-2">
                        {[
                          { value: "once", label: "One-off", hint: "A single donation" },
                          { value: "monthly", label: "Recurring (monthly)", hint: "The same amount every month" },
                        ].map((option) => (
                          <div className="col-sm-6" key={option.value}>
                            <label className={`donate-option d-flex align-items-start gap-2 w-100 ${form.frequency === option.value ? "is-selected" : ""}`}>
                              <input
                                type="radio"
                                name="frequency"
                                className="form-check-input mt-1 flex-shrink-0"
                                value={option.value}
                                checked={form.frequency === option.value}
                                onChange={() => setField("frequency", option.value)}
                              />
                              <span>
                                <span className="fw-bold d-block">{option.label}</span>
                                <span className="small text-muted">{option.hint}</span>
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                      {errors.frequency && <div className="invalid-feedback d-block">{errors.frequency}</div>}
                    </fieldset>

                    <fieldset className="mb-3">
                      <legend className="form-label fw-bold fs-6">
                        Payment amount{monthly ? " (each month)" : ""} <span className="text-danger" aria-hidden="true">*</span>
                      </legend>
                      <div className="row g-2">
                        {PRESETS.map((value) => {
                          const selected = custom === "" && preset === value;
                          return (
                            <div className="col-6 col-sm-3" key={value}>
                              <button
                                type="button"
                                className={`btn donate-amount w-100 fw-bold ${selected ? "is-selected" : ""}`}
                                aria-pressed={selected}
                                onClick={() => {
                                  setPreset(value);
                                  setCustom("");
                                  setErrors((current) => ({ ...current, amount: "" }));
                                }}
                              >
                                ${value}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </fieldset>

                    <div className="mb-4">
                      <label htmlFor="donate-amount" className="form-label fw-bold">
                        Or enter another amount
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          id="donate-amount"
                          type="number"
                          inputMode="numeric"
                          min={min}
                          max={max}
                          step="1"
                          className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                          placeholder={`${min} to ${max.toLocaleString("en-AU")}`}
                          value={custom}
                          onChange={(event) => {
                            setCustom(event.target.value);
                            setErrors((current) => ({ ...current, amount: "" }));
                          }}
                          aria-describedby="donate-amount-help"
                        />
                        <span className="input-group-text">AUD</span>
                      </div>
                      <div id="donate-amount-help" className="form-text">
                        Whole dollars, from ${min} to ${max.toLocaleString("en-AU")}.
                      </div>
                      {errors.amount && <div className="invalid-feedback d-block">{errors.amount}</div>}
                    </div>

                    <div role="alert" aria-live="assertive">
                      {formError && <div className="alert alert-danger small">{formError}</div>}
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="btn red-bg text-white rounded-5 px-4 py-3 fs-5 fw-bold w-100 border-bottom-left"
                    >
                      {submitting
                        ? "Opening secure payment…"
                        : `Donate${amountValid ? ` $${amount.toLocaleString("en-AU")}` : ""}${monthly ? " monthly" : ""}`}
                      {!submitting && <i className="bi bi-heart-fill ms-2" aria-hidden="true"></i>}
                    </button>
                    <p className="small text-muted text-center mt-3 mb-0">
                      <i className="bi bi-lock-fill me-1" aria-hidden="true"></i>
                      You will be taken to Stripe to pay securely by card.
                    </p>

                    <details className="donate-privacy small text-muted mt-3">
                      <summary className="fw-semibold">How we use your details</summary>
                      <ul className="mt-2 mb-0 ps-3">
                        <li>
                          We collect your name, email address and phone number
                          to record your donation and to contact you about it
                          if we need to.
                        </li>
                        <li>
                          Your card details go only to Stripe, our payment
                          provider. WSTSC never sees or stores them.
                        </li>
                        <li>
                          Only the WSTSC committee members who manage donations
                          can see your details. We don't sell or share them.
                        </li>
                        <li>
                          We keep records of completed donations for 7 years as
                          financial records. If you start a donation but don't
                          complete it, your details are deleted within 30 days.
                        </li>
                        <li>
                          To see, correct or delete your details, email{" "}
                          <a href={`mailto:${ORG.email}`} className="red-text fw-semibold">{ORG.email}</a>.
                        </li>
                      </ul>
                    </details>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Donate;
